import { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { theme } from 'coreOld/themes';

interface InputWrapperProps {
  disabled?: boolean;
  children?: ReactNode;
}

interface IconWrapperProps {
  endIcon?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  onMouseDown?: (event: any) => void;
  style?: any;
}

const getBorderStyle = (theme, hasBorder = true, hasError = false) => {
  if (hasError) {
    return {
      border: `1px solid ${theme.colors.dangerBtn}`,
    };
  }

  if (!hasBorder) {
    return {
      border: `1px solid ${theme.palette.neutral.light}`,
    };
  }

  return {
    border: `1px solid ${theme.palette.borderColor}`,
  };
};

const getHoverBorderStyle = (theme, hasBorder = true, hasError = false) => {
  if (hasError) {
    return {
      border: `1px solid ${theme.colors.dangerBtn}`,
    };
  }

  if (!hasBorder) {
    return {
      border: `1px solid ${theme.palette.hover.main}`,
    };
  }

  return {
    border: `1px solid ${theme.palette.hover.borderColor}`,
  };
};

const getFocusedBorderStyle = (theme, hasBorder = true, hasError = false, isFocused = false) => {
  if (!isFocused) {
    return {};
  }

  return getHoverBorderStyle(theme, hasBorder, hasError);
};

const getDisabledStyle = (isDisabled: boolean) => {
  if (!isDisabled) {
    return {};
  }

  return {
    color: theme.palette.neutral.darkest,
    fontSize: 'inherit',
    fontWeight: 500,
    border: 'none',
    backgroundColor: 'inherit',
    textAlign: 'right',
  };
};

const getFontStyle = (fontInherit: boolean, fontSize?: number | string) => {
  if (fontInherit) {
    return {
      fontSize: 'inherit',
    };
  }

  return {
    fontSize: fontSize || 12,
  };
};

export const createCustomStyles = (
  theme,
  size?: string,
  controlStyle?: CSSProperties,
  optionStyle?: CSSProperties,
  menuListStyle?: CSSProperties,
  fontSize?: string | number,
  hasIcon?: boolean,
  hasEndIcon?: boolean,
  fontInherit?: boolean,
  hasBorder?: boolean,
  hasError?: boolean
) => ({
  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    boxShadow: 'none',
    borderTop: 'none',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    width: '100%',
    maxWidth: 500,
    ...getBorderStyle(theme, hasBorder),
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 142,
    zIndex: 1000,
    ...menuListStyle,
  }),
  loadingMessage: (base) => {
    if (optionStyle) {
      return {
        ...base,
        ...optionStyle,
        textAlign: 'center',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'default',
      };
    }

    return {
      ...base,
      ...getFontStyle(fontInherit, fontSize),
      cursor: 'default',
    };
  },
  noOptionsMessage: (base) => {
    if (optionStyle) {
      return {
        ...base,
        ...optionStyle,
        textAlign: 'center',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'default',
      };
    }

    return {
      ...base,
      ...getFontStyle(fontInherit, fontSize),
      cursor: 'default',
    };
  },
  input: (provided) => ({
    ...provided,
    fontWeight: 400,
    ...getFontStyle(fontInherit, fontSize),

    ':after': {
      visibility: 'initial',
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999, maxHeight: 200 }),
  indicatorSeparator: () => ({ display: 'none' }),
  control: (provided, { isDisabled, isFocused }) => ({
    ...provided,
    borderRadius: 0,
    minHeight: theme.inputHeight[size] || 45,
    boxShadow: 'none',
    paddingLeft: hasIcon && '30px',
    ...getBorderStyle(theme, hasBorder, hasError),
    ...getDisabledStyle(isDisabled),
    ...controlStyle,
    ...getFocusedBorderStyle(theme, hasBorder, hasError, isFocused),
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,

    '&:hover,&:focus,&:active': {
      boxShadow: 'none',
      ...getHoverBorderStyle(theme, hasBorder, hasError),
    },
  }),
  placeholder: (provided, { isDisabled, isSelected }) => ({
    ...provided,
    color: theme.palette.text.placeholder,
    fontWeight: (isSelected && theme.fonts.dropdownHover?.fontWeight) || 400,
    ...getFontStyle(fontInherit, fontSize),
    ...getDisabledStyle(isDisabled),
  }),
  option: (provided, state) => {
    return {
      ...provided,
      color: state.isSelected
        ? theme.fonts.dropdownHover?.color || theme.palette.text.secondary
        : theme.palette.text.dropdown,
      backgroundColor: state.isSelected ? theme.colors.dropdownHover : 'transparent',
      textDecoration: state.isSelected && theme.fonts.dropdownHover?.textDecoration,
      fontStyle: state.isSelected && theme.fonts.dropdownHover?.fontStyle,
      fontWeight: (state.isSelected && theme.fonts.dropdownHover?.fontWeight) || 400,
      ...getFontStyle(
        fontInherit,
        fontSize || (state.isSelected && theme.fonts.dropdownHover?.fontSize)
      ),
      ...optionStyle,

      '&:hover, &:focus': {
        backgroundColor: theme.colors.dropdownHover,
        textDecoration: theme.fonts.dropdownHover?.textDecoration,
        fontSize: theme.fonts.dropdownHover?.fontSize,
        fontWeight: theme.fonts.dropdownHover?.fontWeight,
        fontStyle: theme.fonts.dropdownHover?.fontStyle,
        color: theme.fonts.dropdownHover?.color || theme.palette.text.secondary,
        cursor: 'pointer',
        ...getFontStyle(fontInherit, theme.fonts.dropdownHover?.fontSize),
      },
    };
  },
  singleValue: (provided, { isDisabled, isSelected }) => ({
    fontWeight: (isSelected && theme.fonts.dropdownHover?.fontWeight) || 400,
    ...provided,
    ...getFontStyle(fontInherit, fontSize),
    ...getDisabledStyle(isDisabled),
  }),
  multiValue: (provided, { isDisabled }) => ({
    ...provided,
    backgroundColor: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primary.dark}`,
    padding: '6px 10px 6px 10px',
    margin: '0 4.5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...getFontStyle(fontInherit),
    ...getDisabledStyle(isDisabled),
  }),
  multiValueLabel: () => ({
    fontSize: '13px',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginRight: 5,
  }),
  multiValueRemove: () => ({
    color: theme.palette.text.secondary,
    fontSize: '9px',
    border: `1px solid ${theme.palette.backgrounds.main}`,
    borderRadius: '50%',

    '&:hover': {
      cursor: 'pointer',
    },
  }),
  loadingIndicator: (provided) => ({
    ...provided,
    marginRight: hasEndIcon && '40px',
  }),
});

export const Wrapper = styled.div`
  margin-bottom: 5px;
`;

export const Div = styled.div`
  margin-bottom: 5px;
  margin-top: 5px;
`;

export const InputWrapper = styled.div<InputWrapperProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const IconWrapper = styled.div<IconWrapperProps>`
  font-size: 16px;
  position: absolute;
  top: -4px;
  z-index: 9;
  margin-left: 10px;
  transform: translateY(60%);
  color: ${({ theme }) => theme.palette.text.primary};

  ${({ endIcon }) =>
    endIcon &&
    css`
      transform: translateY(50%);
      right: 0;
      margin-right: 15px;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}

  :hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }
`;

export const MatchedText = styled.span`
  font-weight: 600;
  font-size: 'inherit';
`;

export const FormatLabelStyled = styled.div`
  display: flex;
  align-items: center;

  div {
    display: flex;
    align-items: center;
    margin-right: 4px;
  }
`;
